// Angular imports
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Inject
} from '@angular/core';
import { catchError, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { get, isEqual, unset } from 'lodash';

// ISPM Imports
import { ContextProviderService } from '@ispm-client/modules/context-provider/services/context-provider.service';
import { FormHandlerService } from '@ispm/dynamic-form/services/form-handler.service';
import { NOTIFICATION_MESSAGE } from "@ispm-client/modules/aggregator/services/consts";
import { ModalLoaderNgjsService } from '@ispm/layout/services/modal-loader-ngjs.service';
import { OrderManagerBaseComponent } from '../common/order-manager-base.component';
import { OrderService } from '@ispm-client/modules/orders/services/order.service';
import { OrderSFTPService } from '../../services/order.sftp.service';
import { Widget } from '@ispm/widgets/decorator/widget.decorator';
import { WorkflowUtilService } from '@ispm-client/modules/aggregator/services/workflow-util.service';
import { IOrder } from '../../interfaces';
import {AuthenticationService} from "@ispm/user/services/authentication.service";
import {TestService} from '@ispm/dashboard/test/services/test.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';
import {WorkflowService} from "@ispm/workflow/services/workflow.service";
import {ActivatedRoute} from "@angular/router";
import { RouterManagerService } from '@ispm/core/services/router-manager.service';

@Component({
	selector: 'ispm-lab-order-manager',
	templateUrl: './lab-order-manager.component.html',
	styleUrls: ['./lab-order-manager.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})

@Widget('labOrderManager')
export class LabOrderManagerComponent extends OrderManagerBaseComponent {
	constructor(
		private orderService: OrderService,
		private cd: ChangeDetectorRef,
		public notificationMessageService: NotificationMessageService,
		public notificationMessage: NOTIFICATION_MESSAGE,
		private formHandlerService: FormHandlerService,
		private modalLoaderService: ModalLoaderNgjsService,
		protected _authService: AuthenticationService,
		private workflowUtilService: WorkflowUtilService,
		private contextProviderService: ContextProviderService,
		protected testService: TestService,
		private orderSFTPService: OrderSFTPService,
		private workflowService: WorkflowService,
   		private _activatedRoute: ActivatedRoute,
		private routeManagerService: RouterManagerService
	) {
		  super(orderService, cd, notificationMessageService, notificationMessage, formHandlerService,
			  modalLoaderService, _authService, workflowUtilService, contextProviderService, testService, orderSFTPService, _activatedRoute,routeManagerService);
	  }


	ngOnInit() {
		this.onComponentInit();
		this.listenToBiodesixSftpEvent();
	}


	/**
	 * listens to the biodesix SFTP event from parent component to trigger
	 */
	private listenToBiodesixSftpEvent() {
		this.onBiodesixSftpEvent$.pipe(takeUntil(this.asyncdestroyer.asObservable())).subscribe((value: boolean) => {
			if (value) {
				this.postBiodesixSFTPRequest();
			}
		})
	}

	/**
	 * @method onSaveOrderContent
	 * @param formData
	 */
	protected async saveOrderContent(formData: any) {
		this.hasUpdatedFields = true;
		const data = await this.getOrderDetailData(formData);
		if (!(this.checkIfContentValid(data.contentToCreate) && this.checkIfContentValid(data.contentToUpdate))) {
			const promptPayload = {
				title: get(this.config, 'create.submitModel.title', 'Warning'),
				content: get(this.config, 'create.submitModel.content', 'Blank rows will not be saved'),
				decline: 'Cancel'
			};
			await this.showPrompt(promptPayload);
		}
		else {
			this.orderService.setLoader(true);
			try {
				const data = await this.getOrderDetailData(formData);
				let created: Observable<any[]> = await this.getOrderDetailsCreateContent(data, true);
				let updated: Observable<any[]> = await this.getOrderDetailsUpdatedContent(data, true);
				this.saveOrderDetails(created, updated, get(formData, 'dueDate'));
			} catch (e) {
				this.orderService.setLoader(false);
			}
		}
	}

	/**
	 * @method onSaveOrderContent
	 * @param formData
	 */
	public onSaveOrderContent(formData: any) {
		const structuredFormData = this.orderService.removeFallbackKeysFromForm(formData);
		this.saveOrderContent(structuredFormData);
	}

	/**
	 * Update the order info in the backend resources
	 */
	private updateOrderInfoAfterPartialSave() {
		this.orderService.getSingleOrder(this.selectedOrder)
			.pipe(
				takeUntil(this.asyncdestroyer.asObservable()),
				tap((order: IOrder) => {
					unset(order, 'resourceType');
					unset(this.selectedOrder, 'resourceType');
				}),
				switchMap((order: IOrder) => {
					if (!isEqual(this.selectedOrder, order)) {
						return this.updateOrderInDB(this.selectedOrder)
					}
					else {
						return of(this.selectedOrder);
					}
				})
			)
			.subscribe((order: IOrder | null) => {
				if (order) {
					this.orderService.updateOrderStore(order);
					this.selectedOrder.meta = order.meta;
					this.orderService.setSelectedOrder(this.selectedOrder);
					this.hasUpdatedFields = false;
					this.orderSaveInProgress = false;
					this.notificationMessageService.success(this.notificationMessage.ORDER_DETAILS_SAVED_SUCCESSFULLY);
					this.orderService.setLoader(false);
				}
				this.orderService.setLoader(false);
			}, (error) => {
				this.notificationMessageService.error(this.notificationMessage.FAILED_TO_UPDATE_ORDER);
				this.orderService.setLoader(false);
			});
	}

	/**
	 * Saves the current form data to diagnostic resource with out performing any validation.
	 * @param formData holds the form data.
	 */
	public async partialSaveOrderContent(formData: any) {
		this.orderService.setLoader(true);
		try {
			const structuredFormData = this.orderService.removeFallbackKeysFromForm(formData);
			const data = await this.getOrderDetailData(structuredFormData);
			let created: Observable<any[]> = await this.getOrderDetailsCreateContent(data, false);
			let updated: Observable<any[]> = await this.getOrderDetailsUpdatedContent(data, false);
			this.saveCreatedAndUpdatedInfoOfOrderDetails(created, updated).pipe(
				takeUntil(this.asyncdestroyer.asObservable()))
				.subscribe(async (orderDetails) => {
					if (orderDetails && orderDetails.length) {
						orderDetails = this.merger(this.selectedOrderDetails, orderDetails, 'id');
						this.updateSelectedOrderDetailsByLookingDueDate('dueDate', orderDetails, this.selectedOrder).then((order: IOrder) => {
							this.selectedOrder = order;
							const formWithValidWorkflowReqId = this.orderService.validateWorkflowRequestId(orderDetails);
							orderDetails = this.orderService.removeUnwantedDataFromOrderDetails(formWithValidWorkflowReqId);
							this.orderService.updateOrderDetailsStore(orderDetails, get(this.selectedOrder, 'category.code', null));
							this.updateOrderInfoAfterPartialSave();
						}).catch((error) => {
							this.notificationMessageService.error(error || this.notificationMessage.FAILED_TO_SAVE_ORDER_DETAILS);
							this.orderService.setLoader(false);
						});
					}
				}, (error) => {
					this.notificationMessageService.error(error || this.notificationMessage.FAILED_TO_SAVE_ORDER_DETAILS);
					this.orderService.setLoader(false);
				});
		} catch (e) {
			this.orderService.setLoader(false);
		}
	}

	/**
	 * @method postBiodesixSFTPRequest
	 * @description Handles SFTP request and update of resources
	 */
	private postBiodesixSFTPRequest() {
		this.orderService.setLoader(true);
		this.orderSFTPService.copyToSFTP(this.selectedOrderDetails[0]).pipe(
			switchMap((_) => {
				this.orderService.setLoader(true);
				return this.orderSFTPService.updateResources(this.selectedOrderDetails).pipe(
					switchMap(val => {
						this.orderService.setLoader(true);
						this.workflowService.getPatientAnalyses(this.routeManagerService.parameters, false, true);
						this.biodesixOrderDetails$ = this.biodesixOrderDetails$.pipe(
							switchMap(orders => {
								this.selectedOrderDetails = val['orderDetailsResponse'];
								this.notificationMessageService.success('Order details posted successfully!');
								this.orderService.setLoader(false);
								return of(orders);
							})
						);
						this.cd.markForCheck();
						return of(val);
					}),
					catchError(
						(err) => {
							console.error('Failed to update order status in resources : Error : ',err.message);
							return err;
						}
					)
				);
			}),
			catchError(
				(err) => {
					this.notificationMessageService.error('Error in sending biodesix form - please re-submit');
					console.error('Error while posting file on SFTP server : Error : ',err.message);
					return this.orderSFTPService.createProgression(this.selectedOrderDetails[0], false);
				}
			),
			finalize(() => {
				this.orderService.setLoader(false);
				this.hasUpdatedFields = false;
				this.orderSaveInProgress = false;
			})
		).subscribe();
	}
	/**
	 * @description: Handles popup to create the order
	 * @method showCreateOrderPopup
	 */
	 showCreateOrderPopup() {
		this.formHandlerService.popupDynamicFormModal(
			get(this.config, 'create.title'),
			get(this.config, 'create.formId'),
			{},
			get(this.config, 'create.addBtnLabel', 'Add'),
			get(this.config, 'create.cancelBtnLabel', 'Cancel'),
			{},
			OrderService.ModalLoaderTunnelName
		).take(1).subscribe(
			data => {
				let enableBiodesixOrder = true;
				if (get(data, 'payload.formData.category.code') === 'biodesix') {
					let biodesixOrdersData = this.orders.filter(order => order.category.code == 'biodesix');
					if(biodesixOrdersData.length > 0){
						let biodesixOrdersDataInactive = biodesixOrdersData.filter(order => order.isActive.length == 0);
						let biodesixOrdersDataStatusInProcess = biodesixOrdersData.filter(order => order.status.code == 'INITIAL' || order.status.code == 'IN_PROCESS' || order.status.code == 'ERROR' );
						if((biodesixOrdersDataInactive.length > 0) || (biodesixOrdersDataStatusInProcess.length > 0)){
							enableBiodesixOrder = false;
						}else{
							enableBiodesixOrder = true;
						}
					}

				}
				if (get(data, 'status') === modalResponse.approve && enableBiodesixOrder) {
					this.orderService.setLoader(true);
					this.createOrder(get(data, 'payload.formData'));
				}
				else if(!enableBiodesixOrder){
					this.notificationMessageService.error(get(this.config, 'create.orderNotification.onDuplicateBiodesixOrderFailure', this.notificationMessage.UNABLE_TO_CREATE_DUPLICATE_BIODESIX_ORDER));
				}
		}
		);
	}

	/**
	 * @method handleChangeSelectedOrder
	 * @description handles actions when different order is selected
	 * @param order
	 */
	async handleChangeSelectedOrder(order: IOrder) {
		// If the clicked order is different from the one currently active do the process
		if (order.id !== this.selectedOrder.id) {
			await this.orderService.getAllOrdersAndStore(
				get(this.config, 'listPanel.dataBinding', []),
				get(this.config, 'listPanel.orderTypesToExclude', [])
			).then(() => {
				this.orderService.setSelectedOrder(order);
			})
		}
	}
}
